import type { CurrencyT, PlanCategory } from '@mentimeter/http-clients';
import useSwr from 'swr';
import { core } from '@mentimeter/http-clients';
import type { StripeIntervalT } from './types';
import {
  calculateDaysUntilSubscriptionEnds,
  formatAmount,
  formatDate,
  formatPercentage,
} from './utils';

export interface SubscriptionUpdatePreviewT {
  purchaseItem: {
    quantity: number;
    amount: string;
    interval: StripeIntervalT;
    intervalCount: number;
    category: PlanCategory;
  };
  prorationItem: {
    quantity: number;
    amount: string;
    formattedUnusedAmountNeutral: string;
    interval: StripeIntervalT;
    intervalCount: number;
    category: PlanCategory;
  };
  tax: string;
  taxPercentage: string;
  prorationDate: number;
  renewalDate: string;
  renewalTotal: string;
  total: string;
  totalExclTax: number;
  subUnitToUnit: number;
  daysUntilSubscriptionEnds: number;
  currency: string;
}

interface LineItem {
  interval: StripeIntervalT;
  interval_count: number;
  period_end: number;
  period_start: number;
  plan_category: PlanCategory;
  quantity: number;
  subtotal_amount: number;
  tax_amount: number;
}

export interface RawSubscriptionUpdatePreview {
  created: number;
  currency: CurrencyT;
  line_items: { purchase_item: LineItem; proration_item: LineItem };
  proration_date: number;
  renewal_subtotal: number;
  subtotal: number;
  tax: number;
  total: number;
}

export interface InvoicePreviewResponseT {
  invoicePreview: SubscriptionUpdatePreviewT | undefined;
  isLoading: boolean;
  error: Error | undefined;
}

const defaultOptions = {
  skip: false,
  revalidateOnFocus: false,
};

export const formatInvoicePreviewResponse = (
  response: RawSubscriptionUpdatePreview,
): SubscriptionUpdatePreviewT | undefined => {
  const {
    line_items: { purchase_item, proration_item },
    currency,
    renewal_subtotal,
    tax,
    subtotal,
    total,
    proration_date,
  } = response;

  const formattedPurchaseItemAmount = formatAmount({
    amount: purchase_item.subtotal_amount,
    currency: currency?.iso_code,
    subUnit: currency?.subunit_to_unit,
  });
  const formattedProrationItemAmount = formatAmount({
    amount: proration_item.subtotal_amount,
    currency: currency?.iso_code,
    subUnit: currency?.subunit_to_unit,
  });
  const formattedProrationItemAmountNeutral = formatAmount({
    amount: Math.abs(proration_item.subtotal_amount),
    currency: currency?.iso_code,
    subUnit: currency?.subunit_to_unit,
  });
  const formattedTaxAmount = formatAmount({
    amount: tax,
    currency: currency?.iso_code,
    subUnit: currency?.subunit_to_unit,
  });
  const formattedTaxPercentage = formatPercentage(tax, subtotal);
  const formattedRenewalTotal = formatAmount({
    amount: renewal_subtotal,
    currency: currency?.iso_code,
    subUnit: currency?.subunit_to_unit,
  });
  const renewalDate = formatDate(purchase_item.period_end);
  const formattedTotal = formatAmount({
    amount: total,
    currency: currency?.iso_code,
    subUnit: currency?.subunit_to_unit,
  });
  const daysUntilSubscriptionEnds = calculateDaysUntilSubscriptionEnds(
    proration_item.period_end,
  );

  // These formatting functions can return undefined or an empty string
  // We know the BE returns all fields if present so this check is to type ensure that all data is present
  if (
    !formattedPurchaseItemAmount ||
    !formattedProrationItemAmount ||
    !formattedProrationItemAmountNeutral ||
    !formattedTaxAmount ||
    !formattedTaxPercentage ||
    !formattedRenewalTotal ||
    !renewalDate ||
    !formattedTotal ||
    !daysUntilSubscriptionEnds
  )
    return undefined;

  return {
    purchaseItem: {
      quantity: purchase_item.quantity,
      amount: formattedPurchaseItemAmount,
      interval: purchase_item.interval,
      intervalCount: purchase_item.interval_count,
      category: purchase_item.plan_category,
    },
    prorationItem: {
      quantity: proration_item.quantity,
      amount: formattedProrationItemAmount,
      // the unused amount is always negative from the BE since it's the amount the customer will get back
      // but sometimes we want to show it without the negative sign
      formattedUnusedAmountNeutral: formattedProrationItemAmountNeutral,
      interval: proration_item.interval,
      intervalCount: proration_item.interval_count,
      category: proration_item.plan_category,
    },
    tax: formattedTaxAmount,
    taxPercentage: formattedTaxPercentage,
    prorationDate: proration_date,
    renewalDate,
    renewalTotal: formattedRenewalTotal,
    total: formattedTotal,
    subUnitToUnit: currency.subunit_to_unit,
    totalExclTax: response.subtotal,
    daysUntilSubscriptionEnds,
    currency: currency.iso_code,
  };
};

interface UseSubscriptionUpdatePreviewPropsT {
  plan?: PlanCategory;
  licenses?: number;
  options?: {
    skip?: boolean;
    revalidateOnFocus?: boolean;
  };
}

export function useSubscriptionUpdatePreview({
  plan,
  licenses,
  options = defaultOptions,
}: UseSubscriptionUpdatePreviewPropsT): InvoicePreviewResponseT {
  const { skip, revalidateOnFocus } = { ...defaultOptions, ...options };
  const shouldFetch = !skip;

  const {
    data: invoicePreview,
    error,
    isLoading,
  } = useSwr(
    shouldFetch ? ['invoice-preview', plan, licenses] : null,
    async () => {
      const { data } = await core().get<RawSubscriptionUpdatePreview>(
        `/payment-services/invoices/preview/subscription-update`,
        { params: { licenses, plan_category: plan } },
      );

      return formatInvoicePreviewResponse(data);
    },
    {
      revalidateOnFocus,
      // Since we are dealing with proration, we need to make sure that the data is always up to date
      // This is why we set the refresh interval to 5 minutes
      refreshInterval: 300_000,
    },
  );

  if (isLoading)
    return { isLoading: true, invoicePreview: undefined, error: undefined };

  if (error) return { isLoading: false, invoicePreview: undefined, error };

  // This should never happen since if there is no error the BE will return an object will all fields populated
  // but if it ever would happen we want to handle it
  if (!invoicePreview)
    return { isLoading: false, invoicePreview: undefined, error: undefined };

  return { isLoading: false, invoicePreview, error: undefined };
}
